/**
 * Search hub matching and ranking (spec §6): case-insensitive over title and
 * second line; title starts with the query, then a word in the title starts
 * with it, then any match; ties go to the most recent.
 */

export function normalizeHubQuery(query: string): string {
  return query.trim().replace(/\s+/g, " ").toLowerCase();
}

function isWordCharacter(character: string): boolean {
  return /[\p{L}\p{N}]/u.test(character);
}

/** 0 title start, 1 word start, 2 anywhere (title, second line or search words), null no match. */
export function matchTier(
  title: string,
  secondLine: string,
  query: string,
  keywords: readonly string[] = [],
): 0 | 1 | 2 | null {
  const needle = normalizeHubQuery(query);
  if (!needle) return 0;
  const haystack = title.toLowerCase();
  let best: 1 | 2 | null = null;
  for (
    let index = haystack.indexOf(needle);
    index !== -1;
    index = haystack.indexOf(needle, index + 1)
  ) {
    if (index === 0) return 0;
    if (!isWordCharacter(haystack[index - 1]!)) best = 1;
    else if (best === null) best = 2;
  }
  if (best !== null) return best;
  const elsewhere = [secondLine, ...keywords].some((text) =>
    text.toLowerCase().includes(needle),
  );
  return elsewhere ? 2 : null;
}

export function rankHubResults<
  T extends {
    title: string;
    secondLine: string;
    time: number;
    keywords?: readonly string[];
  },
>(
  items: readonly T[],
  query: string,
  options: { keepUnmatched?: boolean } = {},
): T[] {
  return items
    .map((item, index) => {
      const tier = matchTier(item.title, item.secondLine, query, item.keywords);
      return { item, index, tier: tier ?? (options.keepUnmatched ? 3 : null) };
    })
    .filter(
      (entry): entry is { item: T; index: number; tier: number } =>
        entry.tier !== null,
    )
    .sort(
      (a, b) =>
        a.tier - b.tier || b.item.time - a.item.time || a.index - b.index,
    )
    .map((entry) => entry.item);
}

/** The first case-insensitive match of the query in `text`, as [start, end). */
export function matchRange(
  text: string,
  query: string,
): [number, number] | null {
  const needle = query.trim().toLowerCase();
  if (!needle) return null;
  const start = text.toLowerCase().indexOf(needle);
  return start === -1 ? null : [start, start + needle.length];
}
